import { DS_SHAPE_SPACING, DS_SPACING_UNITS } from '@calm/evexia-design-tokens';
import { clamp } from '@calm/evexia-web-common';
import { animated, useScroll, useSpring } from '@react-spring/web';
import BackButton from '@rhg/components/back-button';
import {
    Box,
    Grid,
    GridProps,
    SxProps,
    Theme,
    useMediaQuery,
    useTheme,
} from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { PropsWithChildren, useCallback, useRef, useState } from 'react';
import { ProgramBannerImage } from '../../banner-image';
import {
    BANNER_HEIGHT,
    BANNER_IMAGE_BLUR_PX,
    BANNER_IMAGE_HEIGHT_MD_PX,
    BANNER_WIDTH,
} from '../../banner-image/types';

const styleContentContainer = {
    flex: 1,
    width: '100%',
    height: {
        xs: 'auto',
        md: '100%',
    },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    px: {
        sm: DS_SPACING_UNITS.SIDE_SMALL,
        md: DS_SPACING_UNITS.SIDE_MEDIUM,
        lg: DS_SPACING_UNITS.SIDE_LARGE,
    },
    mt: {
        xs: 12,
        sm: 12,
        md: 0,
    },
    '& *': {
        zIndex: 2,
    },
};

const styleLeftColumn = {
    position: 'relative',
    height: BANNER_HEIGHT,
};
const styleBackButton = {
    position: 'absolute',
    top: {
        xs: DS_SHAPE_SPACING.SIDE_SMALL,
        md: DS_SHAPE_SPACING.SIDE_MEDIUM,
        lg: DS_SHAPE_SPACING.SIDE_LARGE,
    },
    left: {
        xs: DS_SHAPE_SPACING.SIDE_SMALL,
        md: DS_SHAPE_SPACING.SIDE_MEDIUM,
        lg: DS_SHAPE_SPACING.SIDE_LARGE,
    },
};

const styleBackground = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: BANNER_WIDTH,
    height: BANNER_HEIGHT,
};

const styleBackgroundBannerImage = {
    width: BANNER_WIDTH,
    height: {
        ...BANNER_HEIGHT,
        md: `${BANNER_IMAGE_HEIGHT_MD_PX + BANNER_IMAGE_BLUR_PX * 2}px`,
    },
};
const styleBackgroundBannerImageMask = {
    filter: {
        xs: undefined,
        sm: undefined,
        md: `blur(${BANNER_IMAGE_BLUR_PX}px)`,
    },
    maskImage: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? 'linear-gradient(90deg, #FFFFFF 49%, rgba(255, 255, 255, 0) 70%)'
            : 'linear-gradient(90deg, #1A1A2C 49%, rgba(26, 26, 44, 0) 70%)',
    '::before': {
        backgroundColor: (theme: Theme) =>
            theme.palette.mode === 'light'
                ? theme.palette.mask.white['80p']
                : theme.palette.mask.black['60p'],
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    },
};
const styleRightBannerImage = {
    maskImage: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 25%)'
            : 'linear-gradient(90deg, rgba(26, 26, 44, 0) 0%, #1A1A2C 25%)',
};

const styleRightBannerImageContainer = {
    position: { xs: 'static', sm: 'static', md: 'absolute', lg: 'static' },
    top: { xs: undefined, sm: undefined, md: 0, lg: undefined },
    right: { xs: undefined, sm: undefined, md: 0, lg: undefined },
};
const styleMaxWidthGrid = {
    flexWrap: 'nowrap',
    position: 'relative',
    '> .MuiGrid-item': {
        maxWidth: '100%',
    },
};

interface ProgramHeaderWrapperProps {
    programImageSrc?: string;
    contentMdSize?: GridProps['md'];
    hideImageMask?: boolean;
    withScrollEffect?: boolean;
    sxGridContainer?: SxProps;
    sxContentContainer?: SxProps;
    fallbackBackRoute?: string;
    hideBackButton?: boolean;
}

export function ProgramHeaderWrapper({
    programImageSrc,
    contentMdSize = 12,
    withScrollEffect = true,
    children,
    sxGridContainer,
    sxContentContainer,
    fallbackBackRoute,
    hideImageMask = false,
    hideBackButton = false,
}: PropsWithChildren<ProgramHeaderWrapperProps>) {
    const theme = useTheme();
    const isSmOrXs = useMediaQuery(theme.breakpoints.down('md'));
    const [ratioBannerShowing, setRatioBannerShowing] = useState(1);
    const bannerImageRef = useRef<Element>(null);
    const style = useSpring({
        opacity: Math.pow(ratioBannerShowing, 2),
    });

    const AnimatedProgramBannerImage = animated(ProgramBannerImage);

    const handleScroll = useCallback(
        (scrollY: number) => {
            const bannerRect = bannerImageRef.current?.getBoundingClientRect();
            const pixelsShowing = (bannerRect?.bottom ?? 0) - (scrollY ?? 0);
            const ratioShowing = clamp(
                pixelsShowing / (bannerRect?.height ?? 1),
                0,
                1
            );
            setRatioBannerShowing(withScrollEffect ? ratioShowing : 1);
        },
        [withScrollEffect]
    );

    useScroll({
        onChange: ({ value: { scrollY } }) => handleScroll(scrollY),
    });

    return (
        <Box>
            {!hideBackButton && (
                <BackButton
                    sx={styleBackButton}
                    fallbackRoute={fallbackBackRoute}
                />
            )}
            <Grid
                container
                direction={isSmOrXs ? 'column-reverse' : 'row'}
                sx={sxAppend(styleMaxWidthGrid, sxGridContainer)}
            >
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={contentMdSize}
                    lg={6}
                    sx={styleLeftColumn}
                >
                    <Box
                        sx={sxAppend(styleContentContainer, sxContentContainer)}
                    >
                        {children}
                    </Box>
                </Grid>
                <Grid
                    item
                    sm={12}
                    xs={12}
                    md={9}
                    lg={6}
                    sx={styleRightBannerImageContainer}
                >
                    <AnimatedProgramBannerImage
                        style={style}
                        programImageSrc={programImageSrc}
                        sxBannerImage={sxAppend(
                            !isSmOrXs && !hideImageMask && styleRightBannerImage
                        )}
                        ref={bannerImageRef}
                    />
                </Grid>
            </Grid>
            {!isSmOrXs && (
                <ProgramBannerImage
                    sx={styleBackground}
                    programImageSrc={programImageSrc}
                    sxBannerImage={sxAppend(
                        styleBackgroundBannerImage,
                        !hideImageMask && styleBackgroundBannerImageMask
                    )}
                />
            )}
        </Box>
    );
}
